import { withTranslation } from '@application/i18n/with_translation';
import { ActionCard, Button, Divider, Flex, Icon, NotificationAlert, SafeArea, Sheet, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { usePatientRepresentation } from '@feature/representation/use-patient-representation';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { Fragment, useState } from 'react';
import styled from 'styled-components';

const InitialsText = styled.span`
	font: ${tokens.text.label.semiBold};
	color: ${tokens.color.content.mutedBrand};
	margin: auto;
`;

const Surface = styled.div`
	background-color: ${tokens.color.bg.surfaceMuted};
	padding: ${tokens.spacing.x2};
`;

export const SelectRepresentablePatientButton = () => {
	const [isSheetOpen, setIsSheetOpen] = useState(false);
	const [isInfoVisible, setIsInfoVisible] = useState(false);
	const { representablePatients, currentlyRepresentedPatient, isLoading, error } = useRepresentablePatients();
	const { startRepresentation } = usePatientRepresentation();

	const toggleInfoVisible = () => {
		setIsInfoVisible((prevState) => !prevState);
	};

	if (currentlyRepresentedPatient && representablePatients.length > 0) {
		// The "stop representing" button will be placed elsewhere in the UI so is not part of this component
		return null;
	}

	if (!isLoading && !error && representablePatients.length === 0) {
		return null;
	}

	return (
		<Fragment>
			<Button variant='secondary' onClick={() => setIsSheetOpen(true)} loading={isLoading}>
				{withTranslation('PW.Component.SelectRepresentablePatient.TriggerButton')}
			</Button>
			<Sheet
				label={withTranslation('PW.Component.SelectRepresentablePatient.SheetTitle')}
				open={isSheetOpen}
				onOpenChange={(open) => setIsSheetOpen(open)}
				variant='half'
			>
				<SafeArea maxWidth='632px' horizontalPadding={tokens.spacing.x2}>
					<Flex gap={tokens.spacing.x2} style={{ width: '100%' }}>
						<Text variant='headingSmall'>{withTranslation('PW.Component.SelectRepresentablePatient.Title')}</Text>
						{error && (
							<NotificationAlert variant='danger'>
								{withTranslation('PW.Component.SelectRepresentablePatient.FailedToFetchPeopleError')}
							</NotificationAlert>
						)}
						{!error && (
							<Text variant='bodyRegular'>
								<Flex direction='row' gap={tokens.spacing.x1} align='center'>
									<Icon type='checkStarFilled' color={tokens.color.focus} />
									<Text variant='labelSmallRegular' color={tokens.color.content.muted}>
										{withTranslation('PW.Component.SelectRepresentablePatient.VerifiedLabel')}
									</Text>
								</Flex>
							</Text>
						)}
						{!error &&
							representablePatients.map((person) => (
								<ActionCard.Root
									key={person.Id}
									onClick={() => startRepresentation(person.Id)}
									icon={<InitialsText>{person.Initials}</InitialsText>}
								>
									<ActionCard.Title>{person.CommonName}</ActionCard.Title>
									<ActionCard.Meta>{person.IdentityNumber}</ActionCard.Meta>
								</ActionCard.Root>
							))}
						<Divider />
						{isInfoVisible && (
							<Surface>
								<Flex direction='column' gap={tokens.spacing.x2}>
									<Text variant={'headingXSmall'}>{withTranslation('PW.Component.SelectRepresentablePatient.InfoTitle')}</Text>
									<Text variant='bodyRegular'>{withTranslation('PW.Component.SelectRepresentablePatient.InfoParagraphOne')}</Text>
									<Text variant={'bodyRegular'}>{withTranslation('PW.Component.SelectRepresentablePatient.InfoParagraphTwo')}</Text>
								</Flex>
							</Surface>
						)}
						<Button variant='ghost' onClick={toggleInfoVisible}>
							{isInfoVisible
								? withTranslation('PW.Component.SelectRepresentablePatient.HideInfoButton')
								: withTranslation('PW.Component.SelectRepresentablePatient.ShowInfoButton')}
						</Button>
					</Flex>
				</SafeArea>
			</Sheet>
		</Fragment>
	);
};
